import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import KnowledgePackLibraryDashboard from '../components/KnowledgePackLibraryDashboard';
import { txt } from '../utils/bilingualText';
import ChipTag from '../components/ui/ChipTag';
import CompactCard from '../components/ui/CompactCard';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import {
  listPacksV3,
  deletePackV3,
  PACK_TYPES,
  PACK_STATUS,
  getPackTypeIcon,
  getPackStatusColor,
  formatDateTime,
} from '../api/knowledgePacksV3';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS, EMZI_SPACING, getHeaderFlat } from '../styles/emziCI';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';



// ============================================================================
// EXPORT ACTIONS COMPONENT
// ============================================================================
const ExportActions = ({ selectedPacks, onExportComplete }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportLang, setExportLang] = useState('en');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [error, setError] = useState(null);

  const handleSingleExport = async (packId) => {
    try {
      setError(null);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/v3/packs/${packId}/export?fmt=${exportFormat}&lang=${exportLang}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `pack-export.${exportFormat}`;
      if (contentDisposition) {
        const matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (matches) filename = matches[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.detail || 'Export failed. Please try again.');
    }
  };

  const handleBulkExport = async () => {
    try {
      setExporting(true);
      setError(null);
      setExportProgress({ status: 'creating', progress: 0 });

      const token = secureStorage.getItem('nexus_access_token');
      
      // Create bulk export job
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/v3/packs/export/bulk`,
        {
          pack_ids: selectedPacks,
          format: exportFormat,
          lang: exportLang
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const jobId = createResponse.data.job_id;
      setExportProgress({ status: 'processing', progress: 10, jobId });

      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await axios.get(
            `${BACKEND_URL}/api/v3/packs/exports/${jobId}/status`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          const job = statusResponse.data.job;
          setExportProgress({
            status: job.status,
            progress: job.progress,
            jobId
          });

          if (job.status === 'complete') {
            clearInterval(pollInterval);
            
            // Download the file
            const downloadResponse = await axios.get(
              `${BACKEND_URL}/api/v3/packs/exports/${jobId}/download`,
              {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = downloadResponse.headers['content-disposition'];
            let filename = `packs-export.zip`;
            if (contentDisposition) {
              const matches = /filename="([^"]+)"/.exec(contentDisposition);
              if (matches) filename = matches[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setExporting(false);
            setExportProgress(null);
            setShowExportMenu(false);
            if (onExportComplete) onExportComplete();
          } else if (job.status === 'error') {
            clearInterval(pollInterval);
            setError(job.error_message || 'Export failed');
            setExporting(false);
            setExportProgress(null);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setError('Failed to check export status');
          setExporting(false);
          setExportProgress(null);
        }
      }, 2000); // Poll every 2 seconds

    } catch (err) {
      console.error('Bulk export error:', err);
      setError(err.response?.data?.detail || 'Bulk export failed. Please try again.');
      setExporting(false);
      setExportProgress(null);
    }
  };

  return (
    <div className="relative">
      {/* Export Button */}
      <button
        onClick={() => setShowExportMenu(!showExportMenu)}
        disabled={exporting}
        className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {exporting ? (
          <>
            <span className="animate-spin">⏳</span>
            Exporting...
          </>
        ) : (
          <>
            📥 Export Selected ({selectedPacks.length})
          </>
        )}
      </button>

      {/* Export Menu */}
      {showExportMenu && !exporting && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50"
          style={{ minWidth: '280px' }}
        >
          <div className="space-y-3">
            {/* Format Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Format
              </label>
              <div className="flex gap-2">
                {['pdf', 'docx', 'json'].map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setExportFormat(fmt)}
                    className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded ${
                      exportFormat === fmt
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Language
              </label>
              <div className="flex gap-2">
                {[
                  { value: 'en', label: 'EN' },
                  { value: 'ms', label: 'MS' },
                  { value: 'bi', label: 'EN+MS' }
                ].map((lang) => (
                  <button
                    key={lang.value}
                    onClick={() => setExportLang(lang.value)}
                    className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded ${
                      exportLang === lang.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Export Actions */}
            <div className="pt-2 border-t border-gray-200">
              {selectedPacks.length === 1 ? (
                <button
                  onClick={() => handleSingleExport(selectedPacks[0])}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
                >
                  Download {exportFormat.toUpperCase()}
                </button>
              ) : (
                <button
                  onClick={handleBulkExport}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
                >
                  Export as ZIP ({selectedPacks.length} packs)
                </button>
              )}
            </div>

            {/* Cancel */}
            <button
              onClick={() => setShowExportMenu(false)}
              className="w-full px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      {exportProgress && (
        <div className="mt-2 p-2 bg-white border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="animate-spin text-sm">⏳</span>
            <span className="text-xs font-semibold text-gray-700">
              {exportProgress.status === 'creating' && 'Creating export...'}
              {exportProgress.status === 'queued' && 'Queued...'}
              {exportProgress.status === 'running' && 'Processing...'}
              {exportProgress.status === 'complete' && 'Downloading...'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${exportProgress.progress}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {exportProgress.progress}% complete
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-sm">⚠️</span>
            <div className="flex-1">
              <p className="text-xs font-semibold text-red-700">Export Error</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 text-xs"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Knowledge Pack Manager V3 - Management Console
 * ================================================
 * Complete management interface for V3 Knowledge Packs
 * 
 * Features:
 * - Pack Library Dashboard (category panels)
 * - Category-filtered List View
 * - Graph Visualization
 * - Analytics Dashboard
 * - Bulk Operations
 * - Advanced Filtering
 */
const KnowledgePackManagerV3 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLang();
  const [searchParams] = useSearchParams();
  
  // Check if we're in filtered/category view
  const categoryFilter = searchParams.get('type');
  const isDashboardView = !categoryFilter; // Show dashboard if no type filter

  // Tab State
  const [activeTab, setActiveTab] = useState('library'); // 'library', 'graph'

  // Pack List State (for filtered view)
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View & Filter State
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filters, setFilters] = useState({
    pack_type: categoryFilter || '',
    status: '',
    search: '',
  });

  // Selection State (for bulk operations)
  const [selectedPacks, setSelectedPacks] = useState([]);

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';
  const canCreate = isAdmin || isManagement;
  const canEdit = isAdmin || isManagement;
  const canDelete = isAdmin || isManagement;

  // Redirect if not authorized
  useEffect(() => {
    if (!canCreate && !canEdit) {
      navigate('/dashboard');
    }
  }, [canCreate, canEdit, navigate]);

  // Sync filters with URL params
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      pack_type: categoryFilter || ''
    }));
  }, [categoryFilter]);

  // Fetch packs (only for filtered/list view)
  // Fetch V4 Product Packs directly
  const fetchProductPacksV4 = async (filterParams) => {
    const token = secureStorage.getItem('nexus_access_token');
    
    // Build query params
    const params = new URLSearchParams();
    if (filterParams.status) params.append('status', filterParams.status);
    
    const url = `${BACKEND_URL}/api/v4/product-packs${params.toString() ? '?' + params.toString() : ''}`;
    
    console.log('[Fetch V4 Packs] Calling:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`V4 API failed: ${response.status}`);
    }
    
    const v4Packs = await response.json();
    console.log('[Fetch V4 Packs] Response:', v4Packs);
    
    // Transform V4 packs to V3 format for display
    const transformedPacks = v4Packs.map(pack => {
      // Extract name from V4 structure
      const name = pack.identity?.pack_name || pack.name || { en: 'Untitled', ms: 'Tanpa Tajuk' };
      const description = pack.identity?.description || pack.description || { en: '', ms: '' };
      
      console.log(`[Transform V4 Pack] ${pack.pack_id}:`, {
        name_en: name.en,
        desc_en: description.en,
        has_identity: !!pack.identity,
        identity_desc: pack.identity?.description
      });
      
      return {
        pack_id: pack.pack_id,
        pack_type: 'product',
        name: name,
        description: description,
        status: pack.status || 'draft',
        visibility: pack.visibility || 'internal',
        metadata: {
          tags: pack.tags || [],
          category: pack.category || '',
          created_at: pack.system?.created_at || pack.created_at,
          updated_at: pack.system?.updated_at || pack.updated_at,
          v4_source: true,
          status: pack.status
        },
        created_at: pack.system?.created_at || pack.created_at,
        updated_at: pack.system?.updated_at || pack.updated_at,
        // Include V4 specific data for preview
        ingredients_count: pack.ingredients?.length || 0,
        benefits_count: pack.benefits?.product_benefits?.length || 0
      };
    });
    
    return {
      packs: transformedPacks,
      total: transformedPacks.length
    };
  };

  const fetchPacks = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {
        limit: 100,
        skip: 0,
      };

      if (filters.pack_type) filterParams.pack_type = filters.pack_type;
      if (filters.status) filterParams.status = filters.status;
      if (filters.search) filterParams.search = filters.search;

      console.log('[Fetch Packs] Calling API with params:', filterParams);
      
      let response;
      
      // CONDITIONAL ROUTING: If product type, call V4 directly
      if (filters.pack_type === 'product') {
        console.log('[Fetch Packs] 🚀 Using V4 API for products');
        response = await fetchProductPacksV4(filterParams);
      } else {
        // For other pack types, use V3 API
        console.log('[Fetch Packs] Using V3 API');
        response = await listPacksV3(filterParams);
      }
      
      console.log('[Fetch Packs] Response:', response);
      setPacks(response.packs || []);
      console.log('[Fetch Packs] Packs set to state:', response.packs?.length);
    } catch (err) {
      console.error('[Fetch Packs] Error:', err);
      setError(err.message || 'Failed to load knowledge packs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch packs for library tab when not in dashboard view
    // OR fetch packs for graph tab always (needs all packs to show relationships)
    if (activeTab === 'library' && !isDashboardView) {
      fetchPacks();
    } else if (activeTab === 'graph') {
      // Graph View always needs packs (regardless of dashboard mode)
      fetchPacks();
    }
  }, [filters, activeTab]);

  // Handle pack deletion
  const handleDelete = async (packId) => {
    if (!canDelete) {
      alert('You do not have permission to delete packs');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to archive this pack? It will be hidden from active use.'
    );

    if (!confirmDelete) return;

    try {
      await deletePackV3(packId);
      alert('Pack archived successfully');
      fetchPacks();
    } catch (err) {
      console.error('Failed to delete pack:', err);
      alert(`Failed to archive pack: ${err.message}`);
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle selection
  const toggleSelection = (packId) => {
    setSelectedPacks((prev) =>
      prev.includes(packId) ? prev.filter((id) => id !== packId) : [...prev, packId]
    );
  };

  const selectAll = () => {
    if (selectedPacks.length === packs.length) {
      setSelectedPacks([]);
    } else {
      setSelectedPacks(packs.map((p) => p.pack_id));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div style={{ ...getHeaderFlat(), padding: '12px 16px' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl"
              style={{ boxShadow: EMZI_SHADOWS.sm }}
            >
              📦
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">Knowledge Packs V3</h1>
              <p className="text-xs font-medium opacity-90">Management Console</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <LanguageToggle />
            <button
              onClick={() => navigate('/dashboard')}
              className="text-xs font-semibold px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 shadow-sm">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === 'library'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📚 Pack Library
          </button>
          <button
            onClick={() => setActiveTab('graph')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === 'graph'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🕸️ Graph View
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 pb-20 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 140px)' }}>
        {activeTab === 'library' && (
          isDashboardView ? (
            <KnowledgePackLibraryDashboard />
          ) : (
            <PackLibraryTab
              packs={packs}
              loading={loading}
              error={error}
              viewMode={viewMode}
              setViewMode={setViewMode}
              filters={filters}
              handleFilterChange={handleFilterChange}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              handleDelete={handleDelete}
              fetchPacks={fetchPacks}
              navigate={navigate}
              selectedPacks={selectedPacks}
              setSelectedPacks={setSelectedPacks}
              toggleSelection={toggleSelection}
              selectAll={selectAll}
              categoryFilter={categoryFilter}
            />
          )
        )}

        {activeTab === 'graph' && <GraphViewTab packs={packs} />}
      </div>
    </div>
  );
};

// ============================================================================
// PACK LIBRARY TAB
// ============================================================================
const PackLibraryTab = ({
  packs,
  loading,
  error,
  viewMode,
  setViewMode,
  filters,
  handleFilterChange,
  canCreate,
  canEdit,
  canDelete,
  handleDelete,
  fetchPacks,
  navigate,
  selectedPacks,
  setSelectedPacks,
  toggleSelection,
  selectAll,
  categoryFilter,
}) => {
  
  // Pack type label mapping
  const packTypeLabels = {
    product: 'Product Packs',
    promotion: 'Promotion Packs',
    direction: 'Direction Packs',
    values: 'Values Packs',
    services: 'Services Packs',
    compliance: 'Compliance Packs',
    viral: 'Viral Packs',
    trend: 'Trend Packs',
    ambassador: 'Ambassador Packs',
    health: 'Health Packs',
    format: 'Format Packs'
  };
  
  return (
    <>
      {/* Breadcrumb (if in category view) */}
      {categoryFilter && (
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={() => navigate('/knowledge-packs')}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
          >
            ← Back to Dashboard
          </button>
          <div className="text-sm text-gray-500">
            <span>Library</span> 
            <span className="mx-2">›</span>
            <span className="font-semibold text-gray-900">{packTypeLabels[categoryFilter] || categoryFilter}</span>
          </div>
        </div>
      )}
      
      {/* Filters & Controls */}
      <div className="bg-white rounded-xl shadow-sm p-3 mb-3" style={{ borderRadius: EMZI_RADIUS.md }}>
        {/* Search */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="🔍 Search packs..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter Row */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {/* Type Filter */}
          <select
            value={filters.pack_type}
            onChange={(e) => handleFilterChange('pack_type', e.target.value)}
            className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            {Object.values(PACK_TYPES).map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          {/* View Mode Toggle */}
          <div className="flex gap-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              List
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* Only show Create button when inside a specific pack type panel (not in general library) */}
          {canCreate && categoryFilter && (
            <button
              onClick={async () => {
                // Issue #4 Fix: Use V4 editor for product packs
                if (categoryFilter === 'product') {
                  // Create new V4 product pack
                  try {
                                        const newPackData = {
                      pack_type: 'product',
                      status: 'draft',
                      identity: {
                        pack_name: 'New Product Pack',
                        product_name: {
                          en: 'Untitled Product Pack',
                          ms: 'Produk Pack Tanpa Tajuk'
                        },
                        description: {
                          en: '',
                          ms: ''
                        },
                        full_description: '',
                        unique_selling_points: [],
                        target_customer_profile: '',
                        short_description: ''
                      },
                      product_formulation: {
                        ingredients: []
                      },
                      benefits: {
                        product_benefits: []
                      },
                      social: {
                        customer_testimonials: [],
                        trust_badges: []
                      },
                      commercial: {
                        pricing: []
                      },
                      research: {
                        research_studies: []
                      },
                      system: {
                        created_by: 'current_user',
                        created_at: new Date().toISOString(),
                        current_version: 1,
                        version_history: []
                      }
                    };
                    
                    const response = await fetch(`${BACKEND_URL}/api/v4/product-packs`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                      },
                      body: JSON.stringify(newPackData)
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to create pack');
                    }
                    
                    const data = await response.json();
                    // Navigate to V4 editor
                    navigate(`/product-packs/editor/${data.pack_id}`);
                  } catch (error) {
                    console.error('Error creating product pack:', error);
                    toast.error('Failed to create product pack');
                  }
                } else if (categoryFilter === 'ambassador') {
                  // Route ambassador packs to V4 ambassador editor
                  navigate('/ambassador-packs/v4/create');
                } else {
                  // Use V3 wizard for other pack types
                  const url = categoryFilter 
                    ? `/knowledge-packs/wizard?type=${categoryFilter}`
                    : '/knowledge-packs/wizard';
                  navigate(url);
                }
              }}
              className="flex-1 py-2.5 text-sm font-bold rounded-lg shadow-md transition"
              style={{
                background: `linear-gradient(to right, ${EMZI_COLORS.accent}, #FF7700)`,
                color: 'white',
              }}
            >
              + Create {categoryFilter ? packTypeLabels[categoryFilter] : 'Pack'}
            </button>
          )}
          <button
            onClick={fetchPacks}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition"
          >
            🔄
          </button>
        </div>

        {/* Bulk Selection Info + Export Actions */}
        {selectedPacks.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-blue-700">
                {selectedPacks.length} pack(s) selected
              </span>
              <button
                onClick={() => setSelectedPacks([])}
                className="text-xs font-semibold text-blue-600 underline"
              >
                Clear
              </button>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex gap-2 flex-wrap">
              <ExportActions 
                selectedPacks={selectedPacks} 
                onExportComplete={() => setSelectedPacks([])}
              />
              
              {/* Bulk Permanent Delete Button */}
              <button
                onClick={async () => {
                  // Detect pack type from selected packs
                  const selectedPackObjects = packs.filter(p => selectedPacks.includes(p.pack_id));
                  const isAmbassador = selectedPackObjects.length > 0 && selectedPackObjects[0].pack_type === 'ambassador';
                  const packTypeName = isAmbassador ? 'ambassador' : 'product';
                  
                  const password = prompt(`⚠️ PERMANENT DELETE - This action cannot be undone!\n\nYou are about to permanently delete ${selectedPacks.length} ${packTypeName} pack(s).\n\nEnter password to confirm:`);
                  
                  if (!password) {
                    return; // User cancelled
                  }
                  
                  if (password !== 'emzi4111') {
                    alert('❌ Invalid password. Permanent deletion cancelled.');
                    return;
                  }
                  
                  if (window.confirm(`⚠️ FINAL CONFIRMATION\n\nAre you absolutely sure you want to PERMANENTLY DELETE ${selectedPacks.length} ${packTypeName} pack(s)?\n\nThis action CANNOT be undone!`)) {
                    try {
                                            
                      // Choose the correct endpoint based on pack type
                      const endpoint = isAmbassador 
                        ? `${BACKEND_URL}/api/v4/ambassador-packs/bulk-permanent-delete`
                        : `${BACKEND_URL}/api/v4/product-packs/bulk-permanent-delete`;
                      
                      const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                        },
                        body: JSON.stringify({ 
                          pack_ids: selectedPacks,
                          password: password 
                        })
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        alert(`✅ Successfully deleted ${result.deleted_count} pack(s) permanently!`);
                        setSelectedPacks([]);
                        // Refresh the list
                        window.location.reload();
                      } else {
                        const error = await response.json();
                        throw new Error(error.detail || 'Failed to delete');
                      }
                    } catch (error) {
                      alert(`❌ Failed to permanently delete: ${error.message}`);
                      console.error('Bulk permanent delete error:', error);
                    }
                  }
                }}
                className="px-4 py-2 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-md border-2 border-red-800"
              >
                🗑️ Permanent Delete ({selectedPacks.length})
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white rounded-lg shadow-sm p-3 text-center">
          <div className="text-2xl font-black" style={{ color: EMZI_COLORS.primary }}>
            {packs.length}
          </div>
          <div className="text-xs font-medium text-gray-600">Total Packs</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 text-center">
          <div className="text-2xl font-black" style={{ color: EMZI_COLORS.success }}>
            {packs.filter((p) => p.metadata?.status === 'published').length}
          </div>
          <div className="text-xs font-medium text-gray-600">Published</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 text-center">
          <div className="text-2xl font-black text-gray-500">
            {packs.filter((p) => p.metadata?.status === 'draft').length}
          </div>
          <div className="text-xs font-medium text-gray-600">Draft</div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-2">⏳</div>
          <p className="text-sm font-medium text-gray-600">Loading packs...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
          <p className="text-sm font-semibold text-red-600">⚠️ {error}</p>
          <button onClick={fetchPacks} className="mt-2 text-xs font-semibold text-red-600 underline">
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && packs.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-5xl mb-3">📦</div>
          <p className="text-base font-bold text-gray-800 mb-1">No Packs Found</p>
          <p className="text-xs text-gray-600 mb-4">
            {filters.search || filters.pack_type || filters.status
              ? 'Try adjusting your filters'
              : 'Create your first Knowledge Pack to get started'}
          </p>
          {canCreate && (
            <button
              onClick={() => {
                // Route to correct editor based on pack type filter
                if (categoryFilter === 'product') {
                  // For product packs, create a new V4 pack and navigate to editor
                  const createProductPack = async () => {
                    try {
                                            const newPackData = {
                        pack_type: 'product',
                        status: 'draft',
                        identity: {
                          pack_name: 'New Product Pack',
                          product_name: {
                            en: 'Untitled Product Pack',
                            ms: 'Produk Pack Tanpa Tajuk'
                          },
                          description: { en: '', ms: '' },
                          full_description: '',
                          unique_selling_points: [],
                          target_customer_profile: '',
                          short_description: ''
                        },
                        product_formulation: { ingredients: [] },
                        benefits: { product_benefits: [] },
                        social: { customer_testimonials: [], trust_badges: [] },
                        commercial: { pricing: [] },
                        research: { research_studies: [] },
                        system: {
                          created_by: 'current_user',
                          created_at: new Date().toISOString(),
                          current_version: 1,
                          version_history: []
                        }
                      };
                      
                      const response = await fetch(`${BACKEND_URL}/api/v4/product-packs`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                        },
                        body: JSON.stringify(newPackData)
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to create pack');
                      }
                      
                      const data = await response.json();
                      navigate(`/product-packs/editor/${data.pack_id}`);
                    } catch (error) {
                      console.error('Error creating product pack:', error);
                      toast.error('Failed to create product pack');
                    }
                  };
                  createProductPack();
                } else if (categoryFilter === 'ambassador') {
                  // Route ambassador packs to V4 ambassador editor
                  navigate('/ambassador-packs/v4/create');
                } else {
                  // Use V3 wizard for other pack types
                  const url = categoryFilter 
                    ? `/knowledge-packs/wizard?type=${categoryFilter}`
                    : '/knowledge-packs/wizard';
                  navigate(url);
                }
              }}
              className="px-6 py-2.5 text-sm font-bold rounded-lg shadow-md transition"
              style={{
                background: `linear-gradient(to right, ${EMZI_COLORS.accent}, #FF7700)`,
                color: 'white',
              }}
            >
              + Create First Pack
            </button>
          )}
        </div>
      )}

      {/* Grid View */}
      {!loading && !error && packs.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 gap-3">
          {packs.map((pack) => (
            <PackCardGrid
              key={pack.pack_id}
              pack={pack}
              canEdit={canEdit}
              canDelete={canDelete}
              onDelete={() => handleDelete(pack.pack_id)}
              navigate={navigate}
              isSelected={selectedPacks.includes(pack.pack_id)}
              onToggleSelection={() => toggleSelection(pack.pack_id)}
            />
          ))}
        </div>
      )}

      {/* List View */}
      {!loading && !error && packs.length > 0 && viewMode === 'list' && (
        <div className="space-y-2">
          {packs.map((pack) => (
            <PackCardList
              key={pack.pack_id}
              pack={pack}
              canEdit={canEdit}
              canDelete={canDelete}
              onDelete={() => handleDelete(pack.pack_id)}
              navigate={navigate}
              isSelected={selectedPacks.includes(pack.pack_id)}
              onToggleSelection={() => toggleSelection(pack.pack_id)}
            />
          ))}
        </div>
      )}
    </>
  );
};

// ============================================================================
// PACK CARD - GRID VIEW
// ============================================================================
const PackCardGrid = ({ pack, canEdit, canDelete, onDelete, navigate, isSelected, onToggleSelection }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <CompactCard
      hoverable
      padding={EMZI_SPACING.compact}
      style={{
        border: isSelected ? `2px solid ${EMZI_COLORS.primary}` : `1px solid ${EMZI_COLORS.border}`,
        backgroundColor: isSelected ? '#F0F7FF' : EMZI_COLORS.background
      }}
    >
      {/* Header - Compact */}
      <div className="flex items-start justify-between" style={{ marginBottom: EMZI_SPACING.sm }}>
        <div className="flex items-center gap-2 flex-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            className="w-4 h-4"
            style={{ accentColor: EMZI_COLORS.primary }}
          />
          <div style={{ fontSize: '20px' }}>{getPackTypeIcon(pack.pack_type)}</div>
          <div className="flex-1 min-w-0">
            <h3 
              className="truncate" 
              style={{ 
                fontSize: '14px', 
                fontWeight: '700', 
                color: EMZI_COLORS.textPrimary,
                marginBottom: '4px'
              }}
            >
              {txt(pack.name, 'en', 'Untitled')}
            </h3>
            <div className="flex items-center gap-1.5">
              <ChipTag 
                label={pack.metadata?.status?.toUpperCase() || 'UNKNOWN'} 
                size="sm"
                color={pack.metadata?.status === 'published' ? EMZI_COLORS.success : EMZI_COLORS.warning}
              />
              <span style={{ fontSize: '10px', fontWeight: '500', color: EMZI_COLORS.textMuted }}>
                v{pack.version || 1}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowActions(!showActions)}
          style={{
            color: EMZI_COLORS.textSecondary,
            fontSize: '18px',
            fontWeight: '700',
            border: 'none',
            background: 'none',
            cursor: 'pointer'
          }}
        >
          ⋮
        </button>
      </div>

      {/* Content Preview - Compact */}
      <div style={{ marginBottom: EMZI_SPACING.sm }}>
        <p 
          className="line-clamp-2" 
          style={{ 
            fontSize: '12px', 
            color: EMZI_COLORS.textSecondary,
            marginBottom: EMZI_SPACING.sm
          }}
        >
          {pack.description?.en || pack.description?.ms || pack.identity?.description?.en || pack.identity?.description?.ms || 'No description'}
        </p>
        {pack.metadata?.tags && pack.metadata.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pack.metadata.tags.slice(0, 3).map((tag, idx) => (
              <ChipTag 
                key={`item-${idx}`} 
                label={`#${tag}`} 
                size="sm"
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer - Compact */}
      <div 
        className="flex items-center justify-between" 
        style={{ 
          paddingTop: EMZI_SPACING.sm, 
          borderTop: `1px solid ${EMZI_COLORS.borderLight}` 
        }}
      >
        <div className="flex items-center gap-2">
          <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
            {formatDateTime(pack.metadata?.updated_at)}
          </span>
          {pack.metadata?.v4_source && (
            <span 
              style={{ 
                fontSize: '9px', 
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#E0F2FE',
                color: '#0284C7'
              }}
            >
              V4
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pack.ingredients_count > 0 && (
            <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
              🧪 {pack.ingredients_count}
            </span>
          )}
          {pack.benefits_count > 0 && (
            <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
              ✨ {pack.benefits_count}
            </span>
          )}
          <span style={{ fontSize: '11px', fontWeight: '600', color: EMZI_COLORS.primary }}>
            {pack.pack_type}
          </span>
        </div>
      </div>

      {/* Action Menu - Compact Horizontal Buttons */}
      {showActions && (
        <div 
          className="grid grid-cols-3 gap-2" 
          style={{ 
            marginTop: EMZI_SPACING.sm, 
            paddingTop: EMZI_SPACING.sm, 
            borderTop: `1px solid ${EMZI_COLORS.border}` 
          }}
        >
          {canEdit && pack.metadata?.status !== 'archived' && (
            <button
              onClick={() => {
                const targetPackId = pack.pack_id;
                const isV4 = pack.metadata?.v4_source || false;
                const isProduct = pack.pack_type === 'product';
                const isAmbassador = pack.pack_type === 'ambassador';
                
                console.log('📝 Edit pack:', {
                  pack_id: pack.pack_id,
                  is_v4: isV4,
                  pack_type: pack.pack_type,
                  routing_to: (isProduct && isV4) ? 'V4_PRODUCT_EDITOR' : isAmbassador ? 'V4_AMBASSADOR_EDITOR' : 'V3_WIZARD'
                });
                
                // Route V4 product packs to V4 editor, ALL ambassador packs to V4 editor, others to V3 wizard
                if (isProduct && isV4) {
                  navigate(`/product-packs/editor/${targetPackId}`);
                } else if (isAmbassador) {
                  navigate(`/ambassador-packs/v4/${targetPackId}`);
                } else {
                  navigate(`/knowledge-packs/wizard/${targetPackId}`);
                }
              }}
              style={{
                padding: '6px 8px',
                fontSize: '11px',
                fontWeight: '600',
                color: EMZI_COLORS.primary,
                backgroundColor: '#F0F7FF',
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              ✏️ Edit
            </button>
          )}
          <button
            onClick={() => navigate(`/knowledge-packs/${pack.pack_id}/versions`)}
            style={{
              padding: '6px 8px',
              fontSize: '11px',
              fontWeight: '600',
              color: EMZI_COLORS.textSecondary,
              backgroundColor: EMZI_COLORS.surface,
              border: 'none',
              borderRadius: EMZI_RADIUS.sm,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📜 Ver
          </button>
          {canDelete && pack.metadata?.status !== 'archived' && (
            <button
              onClick={onDelete}
              style={{
                padding: '6px 8px',
                fontSize: '11px',
                fontWeight: '600',
                color: EMZI_COLORS.error,
                backgroundColor: '#FEF2F2',
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🗑️ Del
            </button>
          )}
        </div>
      )}
    </CompactCard>
  );
};

// ============================================================================
// PACK CARD - LIST VIEW (Compact with Flat CI)
// ============================================================================
const PackCardList = ({ pack, canEdit, canDelete, onDelete, navigate, isSelected, onToggleSelection }) => {
  return (
    <CompactCard
      hoverable
      padding={EMZI_SPACING.sm}
      style={{
        border: isSelected ? `2px solid ${EMZI_COLORS.primary}` : `1px solid ${EMZI_COLORS.border}`,
        backgroundColor: isSelected ? '#F0F7FF' : EMZI_COLORS.background
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            className="w-4 h-4"
            style={{ accentColor: EMZI_COLORS.primary }}
          />
          <div style={{ fontSize: '18px' }}>{getPackTypeIcon(pack.pack_type)}</div>
          <div className="flex-1 min-w-0">
            <h3 
              className="truncate" 
              style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: EMZI_COLORS.textPrimary 
              }}
            >
              {txt(pack.name, 'en', 'Untitled')}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <ChipTag 
                label={pack.metadata?.status || 'unknown'} 
                size="sm"
                color={pack.metadata?.status === 'published' ? EMZI_COLORS.success : EMZI_COLORS.warning}
              />
              {pack.metadata?.v4_source && (
                <span 
                  style={{ 
                    fontSize: '9px', 
                    fontWeight: '700',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: '#E0F2FE',
                    color: '#0284C7'
                  }}
                >
                  V4
                </span>
              )}
              <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
                v{pack.version || 1}
              </span>
              {pack.ingredients_count > 0 && (
                <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
                  🧪 {pack.ingredients_count}
                </span>
              )}
              {pack.benefits_count > 0 && (
                <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
                  ✨ {pack.benefits_count}
                </span>
              )}
              <span style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
                {pack.pack_type}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {canEdit && pack.metadata?.status !== 'archived' && (
            <button
              onClick={() => {
                const targetPackId = pack.pack_id;
                const isV4 = pack.metadata?.v4_source || false;
                const isProduct = pack.pack_type === 'product';
                const isAmbassador = pack.pack_type === 'ambassador';
                
                // Route V4 product packs to V4 editor, ALL ambassador packs to V4 editor, others to V3 wizard
                if (isProduct && isV4) {
                  navigate(`/product-packs/editor/${targetPackId}`);
                } else if (isAmbassador) {
                  navigate(`/ambassador-packs/v4/${targetPackId}`);
                } else {
                  navigate(`/knowledge-packs/wizard/${targetPackId}`);
                }
              }}
              style={{
                padding: '6px',
                color: EMZI_COLORS.primary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '16px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#F0F7FF'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ✏️
            </button>
          )}
          {canDelete && pack.metadata?.status !== 'archived' && (
            <button 
              onClick={onDelete}
              style={{
                padding: '6px',
                color: EMZI_COLORS.error,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '16px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#FEF2F2'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              🗑️
            </button>
          )}
        </div>
      </div>
    </CompactCard>
  );
};

// ============================================================================
// GRAPH VIEW TAB (React Flow Implementation)
// ============================================================================
const GraphViewTab = ({ packs }) => {
  const navigate = useNavigate();
  const [nodes, setNodes] = React.useState([]);
  const [edges, setEdges] = React.useState([]);
  const [selectedNode, setSelectedNode] = React.useState(null);
  const [filterPackType, setFilterPackType] = React.useState('all');
  const [filterRelType, setFilterRelType] = React.useState('all');

  // Pack type colors (flat CI)
  const packTypeColors = {
    product: EMZI_COLORS.primary,
    promotion: EMZI_COLORS.accent,
    direction: '#8B5CF6',
    values: '#06B6D4',
    services: '#10B981',
    compliance: '#F59E0B',
    viral: '#EC4899',
    trend: '#6366F1',
  };

  // Build graph from packs
  React.useEffect(() => {
    console.log('[Graph View] Received packs:', packs?.length, packs);
    if (!packs || packs.length === 0) {
      console.log('[Graph View] No packs available');
      return;
    }

    // Filter packs
    let filteredPacks = packs;
    if (filterPackType !== 'all') {
      filteredPacks = filteredPacks.filter(p => p.pack_type === filterPackType);
    }

    // Create nodes
    const graphNodes = filteredPacks.map((pack, idx) => ({
      id: pack.pack_id,
      type: 'default',
      data: { 
        label: pack.name?.en || 'Untitled',
        pack: pack
      },
      position: { 
        x: (idx % 5) * 200 + 50, 
        y: Math.floor(idx / 5) * 150 + 50 
      },
      style: {
        background: EMZI_COLORS.background,
        border: `2px solid ${packTypeColors[pack.pack_type] || EMZI_COLORS.border}`,
        borderRadius: EMZI_RADIUS.md,
        padding: '12px',
        fontSize: '12px',
        fontWeight: '600',
        color: EMZI_COLORS.textPrimary,
        width: 180,
      },
    }));

    // Create edges from relationships
    const graphEdges = [];
    console.log('[Graph View] Filtered packs:', filteredPacks.length);
    filteredPacks.forEach(pack => {
      console.log(`[Graph View] Pack ${pack.pack_id} relationships:`, pack.relationships);
      if (pack.relationships?.linked_packs) {
        pack.relationships.linked_packs.forEach((rel, idx) => {
          // Only add edge if target exists in filtered packs
          if (filteredPacks.find(p => p.pack_id === rel.pack_id)) {
            // Filter by relationship type if specified
            if (filterRelType === 'all' || rel.relationship_type === filterRelType) {
              graphEdges.push({
                id: `${pack.pack_id}-${rel.pack_id}-${idx}`,
                source: pack.pack_id,
                target: rel.pack_id,
                label: rel.relationship_type,
                style: { stroke: EMZI_COLORS.border, strokeWidth: 2 },
                labelStyle: { fontSize: 10, fill: EMZI_COLORS.textMuted },
                labelBgStyle: { fill: EMZI_COLORS.background },
              });
            }
          }
        });
      }
    });

    setNodes(graphNodes);
    setEdges(graphEdges);
  }, [packs, filterPackType, filterRelType]);

  const onNodeClick = (event, node) => {
    setSelectedNode(node.data.pack);
  };

  return (
    <div style={{ position: 'relative', height: '600px' }}>
      {/* Filters */}
      <div 
        style={{ 
          position: 'absolute', 
          top: EMZI_SPACING.md, 
          left: EMZI_SPACING.md, 
          zIndex: 10,
          display: 'flex',
          gap: EMZI_SPACING.sm,
          flexWrap: 'wrap'
        }}
      >
        <select
          value={filterPackType}
          onChange={(e) => setFilterPackType(e.target.value)}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            backgroundColor: EMZI_COLORS.background,
            cursor: 'pointer'
          }}
        >
          <option value="all">All Pack Types</option>
          {Object.values(PACK_TYPES).map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select
          value={filterRelType}
          onChange={(e) => setFilterRelType(e.target.value)}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            backgroundColor: EMZI_COLORS.background,
            cursor: 'pointer'
          }}
        >
          <option value="all">All Relationships</option>
          <option value="requires">Requires</option>
          <option value="recommends">Recommends</option>
          <option value="conflicts_with">Conflicts With</option>
          <option value="inherits_from">Inherits From</option>
          <option value="overrides">Overrides</option>
          <option value="supports">Supports</option>
        </select>
      </div>

      {/* React Flow Graph */}
      {nodes.length > 0 ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={onNodeClick}
          fitView
          style={{ background: EMZI_COLORS.surface }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      ) : (
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            backgroundColor: EMZI_COLORS.surface
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: EMZI_SPACING.md }}>🕸️</div>
            <p style={{ fontSize: '14px', color: EMZI_COLORS.textSecondary }}>
              No packs with relationships found
            </p>
          </div>
        </div>
      )}

      {/* Node Details Drawer */}
      {selectedNode && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '100%',
            backgroundColor: EMZI_COLORS.background,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            padding: EMZI_SPACING.lg,
            boxShadow: EMZI_SHADOWS.lg,
            overflowY: 'auto',
            zIndex: 20
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: EMZI_SPACING.md }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: EMZI_COLORS.textPrimary }}>
              Pack Details
            </h3>
            <button
              onClick={() => setSelectedNode(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: EMZI_COLORS.textSecondary
              }}
            >
              ×
            </button>
          </div>

          <div style={{ marginBottom: EMZI_SPACING.md }}>
            <p style={{ fontSize: '14px', fontWeight: '600', color: EMZI_COLORS.textPrimary }}>
              {selectedNode.name?.en || 'Untitled'}
            </p>
            <ChipTag label={selectedNode.pack_type} size="sm" />
          </div>

          <div style={{ marginBottom: EMZI_SPACING.lg }}>
            <p style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary }}>
              {selectedNode.description?.en || 'No description'}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.sm }}>
            <button
              onClick={() => navigate(`/knowledge-packs/wizard/${selectedNode.pack_id}`)}
              style={{
                padding: '10px',
                backgroundColor: EMZI_COLORS.primary,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ✏️ Open in Wizard
            </button>
            <button
              onClick={() => alert('Duplicate feature coming soon')}
              style={{
                padding: '10px',
                backgroundColor: EMZI_COLORS.surface,
                color: EMZI_COLORS.textPrimary,
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              📋 Duplicate
            </button>
            <button
              onClick={() => alert(JSON.stringify(selectedNode, null, 2))}
              style={{
                padding: '10px',
                backgroundColor: EMZI_COLORS.surface,
                color: EMZI_COLORS.textPrimary,
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🔍 View JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgePackManagerV3;
